<template>
  <div class="metric-admin">
    <div v-if="!canEdit" class="metric-admin__denied">
      <p>You do not have permission to access this page.</p>
    </div>

    <template v-else>
      <div class="metric-admin__header">
        <h1 class="metric-admin__title">Metric Config Admin</h1>
        <p class="metric-admin__subtitle">
          Edit metric meter weightings and default user percentile thresholds.
        </p>
      </div>

      <div class="metric-admin__search">
        <b-input
          v-model="search"
          placeholder="Search metrics..."
          icon="search"
          @input="onSearchInput"
        />
      </div>

      <div v-if="loading" class="metric-admin__loading">
        <b-loading :is-full-page="false" :active="true" />
      </div>

      <div v-else-if="error" class="metric-admin__error">
        <b-notification type="is-danger" :closable="false">
          {{ error }}
        </b-notification>
      </div>

      <table v-else class="table is-fullwidth is-striped is-hoverable metric-admin__table">
        <thead>
          <tr>
            <th>Metric Name</th>
            <th class="metric-admin__col-num">Base</th>
            <th class="metric-admin__col-num">Advanced</th>
            <th class="metric-admin__col-num">User Percentile</th>
            <th class="metric-admin__col-action"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="metric in metrics"
            :key="metric.id"
            :class="{
              'row-dirty': metric._dirty,
              'row-saved': metric._saved,
            }"
          >
            <td class="metric-admin__name">{{ metric.name }}</td>
            <td>
              <b-input
                v-model.number="metric._edit.base"
                type="number"
                min="0"
                size="is-small"
                class="metric-admin__num-input"
                @input="markDirty(metric)"
              />
            </td>
            <td>
              <b-input
                v-model.number="metric._edit.advanced"
                type="number"
                min="0"
                size="is-small"
                class="metric-admin__num-input"
                @input="markDirty(metric)"
              />
            </td>
            <td>
              <b-input
                v-model.number="metric._edit.user_percentile"
                type="number"
                min="0"
                max="100"
                size="is-small"
                class="metric-admin__num-input"
                placeholder="—"
                @input="markDirty(metric)"
              />
            </td>
            <td>
              <b-button
                size="is-small"
                type="is-primary"
                :loading="metric._saving"
                @click="saveMetric(metric)"
              >
                Save
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>

      <p v-if="!loading && !error && metrics.length === 0" class="metric-admin__empty">
        No metrics found.
      </p>
    </template>
  </div>
</template>

<script>
import { apiFetch } from '@frontend/api/api.js';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
import { mapStores } from 'pinia';

let searchTimer = null;

export default {
  name: 'MetricAdminView',

  data() {
    return {
      search: '',
      metrics: [],
      loading: false,
      error: null,
    };
  },

  computed: {
    ...mapStores(useAdvisorStore),
    canEdit() {
      return !!this.advisorStore.advisor?.can_edit_metric_config;
    },
  },

  watch: {
    canEdit(val) {
      if (val && this.metrics.length === 0 && !this.loading) {
        this.fetchMetrics();
      }
    },
  },

  mounted() {
    if (this.canEdit) {
      this.fetchMetrics();
    }
  },

  methods: {
    onSearchInput() {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => this.fetchMetrics(), 300);
    },

    markDirty(metric) {
      metric._dirty = true;
      metric._saved = false;
    },

    async fetchMetrics() {
      this.loading = true;
      this.error = null;
      try {
        const url = `/api/v1/metric-admin/${this.search ? `?search=${encodeURIComponent(this.search)}` : ''}`;
        const res = await apiFetch(url, { method: 'GET' });
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const data = await res.json();
        this.metrics = data.map(m => ({
          ...m,
          _saving: false,
          _dirty: false,
          _saved: false,
          _edit: {
            base: m.metric_meter_weighting?.base ?? null,
            advanced: m.metric_meter_weighting?.advanced ?? null,
            user_percentile: m.metric_meter_weighting?.user_percentile ?? null,
          },
        }));
      } catch (e) {
        this.error = e.message || 'Failed to load metrics.';
      } finally {
        this.loading = false;
      }
    },

    async saveMetric(metric) {
      metric._saving = true;
      try {
        const weighting = { ...metric.metric_meter_weighting };
        if (metric._edit.base !== null && metric._edit.base !== '') {
          weighting.base = Number(metric._edit.base);
        }
        if (metric._edit.advanced !== null && metric._edit.advanced !== '') {
          weighting.advanced = Number(metric._edit.advanced);
        }
        if (metric._edit.user_percentile !== null && metric._edit.user_percentile !== '') {
          weighting.user_percentile = Number(metric._edit.user_percentile);
        } else {
          delete weighting.user_percentile;
        }

        const res = await apiFetch(`/api/v1/metric-admin/${metric.id}/`, {
          method: 'PATCH',
          body: JSON.stringify({ metric_meter_weighting: weighting }),
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.metric_meter_weighting?.join(', ') || `Save failed (${res.status})`);
        }

        const updated = await res.json();
        metric.metric_meter_weighting = updated.metric_meter_weighting;
        metric._edit = {
          base: updated.metric_meter_weighting?.base ?? null,
          advanced: updated.metric_meter_weighting?.advanced ?? null,
          user_percentile: updated.metric_meter_weighting?.user_percentile ?? null,
        };
        metric._dirty = false;
        metric._saved = true;
        setTimeout(() => { metric._saved = false; }, 2000);

        this.$buefy.toast.open({ message: `Saved "${metric.name}"`, type: 'is-success' });
      } catch (e) {
        this.$buefy.toast.open({ message: e.message || 'Save failed.', type: 'is-danger', duration: 5000 });
      } finally {
        metric._saving = false;
      }
    },
  },
};
</script>

<style scoped>
.metric-admin {
  padding: 2rem;
  max-width: 900px;
}

.metric-admin__denied {
  color: #cc0000;
  padding: 2rem;
}

.metric-admin__header {
  margin-bottom: 1.5rem;
}

.metric-admin__title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.metric-admin__subtitle {
  color: #666;
  margin-bottom: 0;
}

.metric-admin__search {
  margin-bottom: 1.5rem;
  max-width: 400px;
}

.metric-admin__loading {
  position: relative;
  min-height: 100px;
}

.metric-admin__error {
  margin-bottom: 1rem;
}

.metric-admin__table {
  font-size: 0.9rem;
}

.metric-admin__name {
  max-width: 380px;
}

.metric-admin__col-num {
  width: 110px;
  text-align: center;
}

.metric-admin__col-action {
  width: 80px;
}

.metric-admin__num-input {
  width: 90px;
}

.metric-admin__empty {
  color: #888;
  font-style: italic;
}

/* Unsaved changes — amber tint */
.row-dirty td {
  background-color: #fff8e1 !important;
}

/* Just saved — green tint, fades after 2 s */
.row-saved td {
  background-color: #e8f5e9 !important;
  transition: background-color 1.5s ease;
}
</style>
